import { CheckIcon, XIcon } from '@primer/octicons-react';
import React from 'react';
import { graphql, useFragment } from 'react-relay';

import {
  PromotionConnectionDataTable_promotionConnection$data,
  PromotionConnectionDataTable_promotionConnection$key,
} from '../../../relay/__generated__/PromotionConnectionDataTable_promotionConnection.graphql';
import DataTable, { DataTableProps } from '../../core/DataTable';
import DateText from '../../core/DateText';
import StyledOcticon from '../../core/StyledOcticon';
import Text from '../../core/Text';
import UserAvatarText from '../../user/UserAvatarText';
import PromotionRepeatText from '../PromotionRepeatText';
import PromotionStatusLabel from '../PromotionStatusLabel';

const PromotionConnectionDataTable_promotionConnection = graphql`
  fragment PromotionConnectionDataTable_promotionConnection on PromotionConnection {
    edges {
      node {
        id

        ...PromotionStatusLabel_promotion
        sequence

        priority
        title
        startAt
        endAt

        isRepeat

        ...PromotionRepeatText_promotion
        createdBy {
          id
          ...UserAvatarText_user
        }
      }
    }
  }
`;

type Props = {
  promotionConnection: PromotionConnectionDataTable_promotionConnection$key;
} & Omit<
  DataTableProps<PromotionConnectionDataTable_promotionConnection$data['edges'][number]['node']>,
  'rows' | 'columns'
>;

const PromotionConnectionDataTable = ({ promotionConnection, ...props }: Props) => {
  const { edges } = useFragment(PromotionConnectionDataTable_promotionConnection, promotionConnection);

  return (
    <DataTable
      rows={edges.map(({ node }) => node).filter((node) => !!node)}
      columns={[
        {
          field: 'status',
          title: '상태',
          renderValue: (promotion) => <PromotionStatusLabel promotion={promotion} />,
          width: 80,
          align: 'center',
        },
        {
          field: 'sequence',
          title: 'ID',
          renderValue: ({ sequence }) => <Text fontSize={1}>{sequence ?? '-'}</Text>,
          width: 80,
        },
        {
          field: 'priority',
          title: '우선순위',
          renderValue: ({ priority }) => <Text fontSize={1}>{priority}</Text>,
          width: 80,
        },
        {
          field: 'title',
          title: '프로모션명',
          renderValue: ({ title }) => <Text fontSize={1}>{title}</Text>,
          width: 341,
        },
        {
          field: 'startAt',
          title: '시작일',
          renderValue: ({ startAt }) => <DateText fontSize={1}>{startAt}</DateText>,
          width: 160,
        },
        {
          field: 'endAt',
          title: '종료일',
          renderValue: ({ endAt }) => <DateText fontSize={1}>{endAt}</DateText>,
          width: 160,
        },
        {
          field: 'repeat',
          title: '주기',
          renderValue: (promotion) => <PromotionRepeatText promotion={promotion} fontSize={1} />,
          width: 103,
        },
        {
          field: 'isRepeat',
          align: 'center',
          title: '반복',
          renderValue: ({ isRepeat }) =>
            isRepeat ? (
              <StyledOcticon icon={CheckIcon} sx={{ color: 'success.fg' }} />
            ) : (
              <StyledOcticon icon={XIcon} sx={{ color: 'danger.fg' }} />
            ),
          width: 43,
        },
        {
          field: 'createdBy',
          title: '제작자',
          renderValue: ({ createdBy }) => <UserAvatarText user={createdBy} />,
          width: 92,
        },
      ]}
      {...props}
    />
  );
};

export default PromotionConnectionDataTable;
